import Form, { ErrorMessage } from '@components/Form'
import { NAV_BAR_BORDER_TOP_WIDTH_PX, NAV_BAR_HEIGHT_PX } from '@components/NavBar'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel
} from '@headlessui/react'
import { createPlanningCffsApi } from 'api-validations/locutus'
import { type FC, useCallback, useEffect, useState } from 'react'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaMutation } from 'shared-ui/api-provider'
import { ButtonKind, Icon, PanoramaButton } from 'shared-ui/components'
import { SpriteMap, SpriteMap16 } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { useLocalization } from 'shared-ui/localization'
import { type Cff, cffKey } from 'shared-ui/operation-wide-data'
import { hasSubstring, isNil } from 'utils'

import {
  BLANK_CFF,
  type CffTarget,
  CffTargetStatus,
  type PlanningField,
  resolveCffTarget
} from '../../lib/planUpload/planEntry'

enum CffLevel {
  Client = 'client',
  Farm = 'farm',
  Field = 'field'
}

enum CffMode {
  Find = 'find',
  Create = 'create'
}

type PickerStep = { mode: CffMode.Find } | { mode: CffMode.Create; seed: Cff }

/**
 * The edge of the trigger that the open field list is attached to.
 */
enum AttachedEdge {
  Top = 'top',
  Bottom = 'bottom'
}

/**
 * Past this many matches, the grower narrows the search instead of scrolling.
 */
const MAX_RESULTS = 50

const triggerClasses = createVariants({
  base: 'group/cff flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1 text-left text-gray-900 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60 data-[open]:border-green',
  variants: {
    attachedEdge: {
      [AttachedEdge.Top]: 'rounded-t-none',
      [AttachedEdge.Bottom]: 'rounded-b-none'
    }
  }
})

const inputClasses = createVariants({
  // The browser's own focus ring ignores the input's outline color.
  base: 'mb-0 h-8 w-full focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500'
})

const LABEL_CLASSES = 'pan-label mb-1 block'

const resultClasses = createVariants({
  base: 'flex min-h-12 w-full shrink-0 items-center gap-2 px-4 py-2 text-left hover:bg-gray-50',
  variants: {
    selected: { true: 'bg-accent-100 text-green hover:bg-accent-100' }
  }
})

const cffOf = ({ client, farm, field }: PlanningField): Cff => ({ client, farm, field })

const matches = (field: PlanningField, term: string) =>
  hasSubstring(field.field, term) ||
  hasSubstring(field.farm, term) ||
  hasSubstring(field.client, term)

const namesUnder = ({
  level,
  fields,
  under
}: {
  level: CffLevel.Client | CffLevel.Farm
  fields: readonly PlanningField[]
  under: Cff
}) => {
  const reachable = fields.filter(
    (field) => level === CffLevel.Client || field.client.trim() === under.client.trim()
  )
  return [...new Set(reachable.map((field) => field[level]))]
    .filter((name) => name !== '')
    .sort((a, b) => a.localeCompare(b))
}

const TargetSummary: FC<{ target: CffTarget }> = ({ target }) => {
  const { t } = useLocalization()

  if (target.status === CffTargetStatus.Unassigned)
    return (
      <span className="pan-detail flex-1 truncate italic text-gray-500">
        {t(($) => $.planning.upload.place)}
      </span>
    )

  return (
    <>
      <span className="pan-truncatable flex-1">
        <span className="pan-body-bold">{target.cff.field}</span>{' '}
        <span className="text-gray-600">
          {target.cff.client} | {target.cff.farm}
        </span>
      </span>
      {target.status === CffTargetStatus.New && (
        <span className="pan-detail shrink-0 rounded bg-marigold-100 px-1.5 py-0.5">
          {t(($) => $.planning.upload.newFieldChip)}
        </span>
      )}
    </>
  )
}

/**
 * Suggests names the operation already uses, but accepts any.
 */
const NameField: FC<{
  level: CffLevel
  value: string
  suggestions: readonly string[]
  onChange: (value: string) => void
}> = ({ level, value, suggestions, onChange }) => {
  const { t } = useLocalization()
  const offered = suggestions.filter((name) => hasSubstring(name, value)).slice(0, 8)

  return (
    <div>
      <label htmlFor={`cff-${level}`} className={LABEL_CLASSES}>
        {t(($) => $.agConcepts[level], { count: 1 })}
      </label>
      <Combobox
        as="div"
        className="relative"
        value={value}
        // Handle null from HeadlessUI
        onChange={(name: string | null) => onChange(name ?? '')}
        immediate
      >
        <ComboboxInput
          id={`cff-${level}`}
          className={inputClasses({ className: 'rounded-md border border-gray-300 px-2' })}
          placeholder={t(($) => $.planning.upload.nameLevel[level])}
          autoComplete="off"
          displayValue={(name: string) => name}
          onChange={(event) => onChange(event.target.value)}
        />
        {offered.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {offered.map((name) => (
              <ComboboxOption
                key={name}
                value={name}
                className="pan-body cursor-pointer px-3 py-1.5 data-[focus]:bg-accent-100"
              >
                {name}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </Combobox>
    </div>
  )
}

/**
 * Creates a new field immediately as opposed to creating upon import (like .2020 cff data).
 */
const NewFieldForm: FC<{
  fields: readonly PlanningField[]
  seed: Cff
  onChange: (target: CffTarget) => void
  onSettled: () => void
  onCancel: () => void
}> = ({ fields, seed, onChange, onSettled, onCancel }) => {
  const { t } = useLocalization()
  const orgCode = useOrgCodeOrThrow()
  const createField = usePanoramaMutation(createPlanningCffsApi)
  const [draft, setDraft] = useState(seed)

  const update = (level: CffLevel, name: string) =>
    setDraft((current) => ({
      ...current,
      [level]: name,
      ...(level === CffLevel.Client && { farm: '', field: '' }),
      ...(level === CffLevel.Farm && { field: '' })
    }))

  const drafted = resolveCffTarget({ cff: draft, fields })

  const confirmDraft = async () => {
    if (drafted.status !== CffTargetStatus.New) return

    await createField.mutateAsync({ params: { orgCode }, body: { fields: [drafted.cff] } })
    onChange(drafted)
    onSettled()
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      <NameField
        level={CffLevel.Client}
        value={draft.client}
        suggestions={namesUnder({ level: CffLevel.Client, fields, under: draft })}
        onChange={(name) => update(CffLevel.Client, name)}
      />
      <NameField
        // Remounting clears the combobox's own input when the client changes.
        key={`farm-${draft.client}`}
        level={CffLevel.Farm}
        value={draft.farm}
        suggestions={namesUnder({ level: CffLevel.Farm, fields, under: draft })}
        onChange={(name) => update(CffLevel.Farm, name)}
      />
      <div>
        <label htmlFor="cff-field" className={LABEL_CLASSES}>
          {t(($) => $.agConcepts.field, { count: 1 })}
        </label>
        <Form.Input
          id="cff-field"
          className={inputClasses()}
          value={draft.field}
          placeholder={t(($) => $.planning.upload.nameLevel.field)}
          onChange={(event) => update(CffLevel.Field, event.target.value)}
        />
      </div>

      {createField.isError && (
        <ErrorMessage className="my-0">
          {t(($) => $.planning.upload.createFieldFailed)}
        </ErrorMessage>
      )}

      <div className="flex gap-2">
        <PanoramaButton kind={ButtonKind.Secondary} className="flex-1" onClick={onCancel}>
          {t(($) => $.buttons.cancel)}
        </PanoramaButton>
        <PanoramaButton
          className="flex-1"
          disabledWhen={[
            {
              when: drafted.status === CffTargetStatus.Unassigned,
              reason: t(($) => $.disabledReasons.fillInRequiredFields)
            },
            {
              when: drafted.status === CffTargetStatus.Existing,
              reason: t(($) => $.planning.upload.fieldExists)
            }
          ]}
          loading={createField.isPending}
          onClick={confirmDraft}
        >
          {t(($) => $.planning.upload.createField)}
        </PanoramaButton>
      </div>
    </div>
  )
}

const FieldList: FC<{
  fields: readonly PlanningField[]
  target: CffTarget
  onChange: (target: CffTarget) => void
  onCreate: (seed: Cff) => void
  onSettled: () => void
}> = ({ fields, target, onChange, onCreate, onSettled }) => {
  const { t } = useLocalization()
  const [search, setSearch] = useState('')

  const chosenKey = target.status === CffTargetStatus.Unassigned ? undefined : cffKey(target.cff)
  const found = fields.filter((field) => matches(field, search))
  const shown = found.slice(0, MAX_RESULTS)

  return (
    <>
      <div className="relative p-2">
        <Form.Input
          type="search"
          className={inputClasses({ className: 'pr-8' })}
          value={search}
          autoFocus
          placeholder={t(($) => $.planning.searchFields)}
          aria-label={t(($) => $.planning.searchFields)}
          onChange={(event) => setSearch(event.target.value)}
        />
        {search === '' && (
          <Icon
            icon={SpriteMap.SearchIcon}
            className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-green-500"
          />
        )}
      </div>

      {found.length === 0 ? (
        <p className="pan-body-small flex-1 px-4 py-6 text-center text-gray-500">
          {t(($) => $.planning.upload.noFieldsMatch)}
        </p>
      ) : (
        <div className="flex max-h-60 flex-1 flex-col overflow-y-auto">
          {shown.map((field) => {
            const selected = cffKey(field) === chosenKey

            return (
              <button
                key={field.id}
                type="button"
                className={resultClasses({ selected })}
                onClick={() => {
                  onChange(resolveCffTarget({ cff: cffOf(field), fields }))
                  onSettled()
                }}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="pan-body pan-truncatable">{field.field}</span>
                  <span className="pan-detail pan-truncatable text-gray-600">
                    {field.client} | {field.farm}
                  </span>
                </span>
                {selected && (
                  <Icon icon={SpriteMap16.CheckCircle16} className="size-4 shrink-0 text-green" />
                )}
              </button>
            )
          })}
          {found.length > shown.length && (
            <p className="pan-detail px-4 py-2 text-gray-500">
              {t(($) => $.planning.upload.andMoreFields, { count: found.length - shown.length })}
            </p>
          )}
        </div>
      )}

      <div className="p-2">
        <PanoramaButton
          kind={ButtonKind.Secondary}
          icon={SpriteMap16.AddCircle16}
          className="w-full"
          onClick={() =>
            // Start the new field with the unmatched search term.
            onCreate({
              ...(target.status === CffTargetStatus.Unassigned ? BLANK_CFF : target.cff),
              ...(search.trim() !== '' && { field: search.trim() })
            })
          }
        >
          {t(($) => $.planning.upload.createNewField)}
        </PanoramaButton>
      </div>
    </>
  )
}

/**
 * Mounted only while the picker is open.
 */
const PickerPanel: FC<{
  fields: readonly PlanningField[]
  target: CffTarget
  onChange: (target: CffTarget) => void
  trigger: HTMLElement | null
  onAttach: (edge: AttachedEdge | undefined) => void
  onSettled: () => void
  onLeftBehind: () => void
}> = ({ fields, target, onChange, trigger, onAttach, onSettled, onLeftBehind }) => {
  const [step, setStep] = useState<PickerStep>({ mode: CffMode.Find })

  useEffect(() => {
    if (isNil(trigger)) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting === false) onLeftBehind()
      },
      // Under the navbar counts as out-of-view.
      { rootMargin: `-${NAV_BAR_HEIGHT_PX + NAV_BAR_BORDER_TOP_WIDTH_PX}px 0px 0px 0px` }
    )
    observer.observe(trigger)
    return () => observer.disconnect()
  }, [trigger, onLeftBehind])

  // The panel may flip above the trigger, and the trigger squares the corners of whichever edge it joins.
  const observeAttachment = useCallback(
    (node: HTMLElement | null) => {
      const panel = node?.parentElement
      if (isNil(panel)) return
      const readEdge = () =>
        onAttach(
          (panel.getAttribute('data-anchor') ?? '').startsWith('top')
            ? AttachedEdge.Top
            : AttachedEdge.Bottom
        )
      readEdge()
      const observer = new MutationObserver(readEdge)
      observer.observe(panel, { attributes: true, attributeFilter: ['data-anchor'] })
      return () => {
        observer.disconnect()
        onAttach(undefined)
      }
    },
    [onAttach]
  )

  return (
    <div
      ref={observeAttachment}
      // Tall enough for the new-field form. This reduces the frequency of the panel flipping over the trigger.
      className="flex min-h-64 w-[--button-width] flex-col border border-green bg-white group-data-[anchor~=bottom]/panel:rounded-b-lg group-data-[anchor~=top]/panel:rounded-t-lg group-data-[anchor~=bottom]/panel:border-t-0 group-data-[anchor~=top]/panel:border-b-0"
    >
      <div
        aria-hidden
        className="pointer-events-none h-px shrink-0 bg-gray-200 group-data-[anchor~=top]/panel:order-last"
      />

      {step.mode === CffMode.Create ? (
        <NewFieldForm
          fields={fields}
          seed={step.seed}
          onChange={onChange}
          onSettled={onSettled}
          onCancel={() => setStep({ mode: CffMode.Find })}
        />
      ) : (
        <FieldList
          fields={fields}
          target={target}
          onChange={onChange}
          onCreate={(seed) => setStep({ mode: CffMode.Create, seed })}
          onSettled={onSettled}
        />
      )}
    </div>
  )
}

type Props = {
  fields: readonly PlanningField[]
  target: CffTarget
  disabled: boolean
  onChange: (target: CffTarget) => void
}

/**
 * Places an asset in a new or existing field.
 */
export const CffPicker: FC<Props> = ({ fields, target, disabled, onChange }) => {
  const [attachedEdge, setAttachedEdge] = useState<AttachedEdge>()
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)

  return (
    <Popover className="min-w-0 flex-1">
      <PopoverButton
        ref={setTrigger}
        className={triggerClasses({ attachedEdge })}
        disabled={disabled}
        title={
          target.status === CffTargetStatus.Unassigned
            ? undefined
            : `${target.cff.field} | ${target.cff.farm} | ${target.cff.client}`
        }
      >
        <TargetSummary target={target} />
        <Icon
          icon={SpriteMap.ChevronIcon}
          className="size-3 flex-none rotate-180 text-gray-400 transition-transform group-data-[open]/cff:rotate-0 group-data-[open]/cff:text-green-500"
        />
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: 'bottom end', gap: '-1px' }}
        className="group/panel z-[100] focus:outline-none"
      >
        {({ close }) => (
          <PickerPanel
            fields={fields}
            target={target}
            onChange={onChange}
            trigger={trigger}
            onAttach={setAttachedEdge}
            onSettled={() => close()}
            // Avoid returning focus to the trigger.
            onLeftBehind={() => close(document.body)}
          />
        )}
      </PopoverPanel>
    </Popover>
  )
}
